import * as React from "react";
import { cn } from "../../lib/utils";

interface TooltipContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  tooltipId: string;
}

let tooltipIdCounter = 0;
const TooltipContext = React.createContext<TooltipContextValue | undefined>(undefined);

interface TooltipProviderProps {
  children: React.ReactNode;
}

export function TooltipProvider({ children }: TooltipProviderProps) {
  const [open, setOpen] = React.useState(false);
  const [tooltipId] = React.useState(() => `tooltip-${++tooltipIdCounter}`);

  return (
    <TooltipContext.Provider value={{ open, setOpen, tooltipId }}>
      {children}
    </TooltipContext.Provider>
  );
}

interface TooltipProps {
  children: React.ReactNode;
}

export function Tooltip({ children }: TooltipProps) {
  return <div className="relative inline-block">{children}</div>;
}

interface TooltipTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

export function TooltipTrigger({ asChild, children, ...props }: TooltipTriggerProps) {
  const context = React.useContext(TooltipContext);
  if (!context) throw new Error("TooltipTrigger must be used within TooltipProvider");

  const handleOpen = () => context.setOpen(true);
  const handleClose = () => context.setOpen(false);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && context.open) {
      context.setOpen(false);
    }
  };

  const sharedProps = {
    onMouseEnter: handleOpen,
    onMouseLeave: handleClose,
    onFocus: handleOpen,
    onBlur: handleClose,
    onKeyDown: handleKeyDown,
    tabIndex: 0,
    'aria-describedby': context.open ? context.tooltipId : undefined,
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...sharedProps,
      ...props,
    } as any);
  }

  return (
    <div {...sharedProps} {...props}>
      {children}
    </div>
  );
}

interface TooltipContentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export function TooltipContent({ className, children, ...props }: TooltipContentProps) {
  const context = React.useContext(TooltipContext);
  if (!context) throw new Error("TooltipContent must be used within TooltipProvider");

  if (!context.open) return null;

  // Check if dark mode is active
  const isDarkMode = document.documentElement.classList.contains('dark');

  return (
    <div
      id={context.tooltipId}
      role="tooltip"
      className={cn(
        "absolute bottom-full left-1/2 mb-2 z-50 rounded-md border px-4 py-2 text-xs shadow-lg animate-in fade-in-0 zoom-in-95 pointer-events-none tooltip-content",
        "-translate-x-1/2",
        className
      )}
      style={{ 
        willChange: 'transform, opacity',
        transform: 'translateX(-50%) translateZ(0)',
        maxWidth: '24rem',
        minWidth: '16rem',
        backgroundColor: isDarkMode ? 'rgb(15, 23, 42)' : 'rgb(255, 255, 255)',
        color: isDarkMode ? 'rgb(209, 213, 219)' : 'rgb(75, 85, 99)',
        opacity: 1,
        backdropFilter: 'none',
      }}
      {...props}
    >
      {children}
    </div>
  );
}

